import { pujaPalette } from '../theme/pujaTheme';

/**
 * Tema del micrositio: tipografías, alto de cabecera y los neutros.
 *
 * Vive acá y no en cada página porque estaba copiado en las CUATRO
 * (RematePage, AnimalPage, ContactoPage, CondicionesPage) palabra por palabra.
 * Agregar un combo tipográfico o una cabecera nueva obligaba a tocar cuatro
 * archivos, y al que se olvidaba no le avisaba nada: la página caía al default
 * en silencio.
 *
 * Es el mismo criterio de `utils/escalonOferta.js` y `utils/montoInput.js`,
 * donde tener las cuentas copiadas ya salió caro una vez.
 */

// OJO: estos combos deben coincidir con los del backend, que es quien los
// ofrece en el editor del panel. Si se agrega uno, va en los dos lados.
export const TIPOGRAFIAS = {
  moderna: { title: "'Montserrat', sans-serif", body: "'Open Sans', sans-serif" },
  clasica: { title: "'Playfair Display', serif", body: "'Lato', sans-serif" },
  rural:   { title: "'Oswald', sans-serif", body: "'Source Sans Pro', sans-serif" },
};

// Alto del header en DESKTOP según el tipo de cabecera que eligió el productor.
// En mobile colapsa a una fila de ~60px: ese tope lo aplica la regla
// `.micrositio-page .hero-zone` de index.css, no este mapa.
export const HEADER_HEIGHT = {
  'cabecera-1': 76,
  'cabecera-2': 76,
  'cabecera-3': 120,
  'cabecera-4': 120,
};

export const tipografiaDe = (config) =>
  TIPOGRAFIAS[config?.tipografia_combo] || TIPOGRAFIAS.moderna;

export const altoHeaderDe = (config) =>
  HEADER_HEIGHT[config?.cabecera || 'cabecera-1'] || 76;

/**
 * Cómo llama la cabaña al grupo que se vende junto: "Corral" o "Lote". Lo
 * elige el productor en Herramientas → Micrositio → Estructura
 * (`config.termino_lote`, 'corral' | 'lote'); el número es el mismo campo
 * `animal.corral` de siempre, sólo cambia la palabra con la que se muestra.
 * Default 'corral', así ningún micrositio publicado cambia solo.
 *
 * Devuelve las tres formas que usan las páginas, para que ningún texto arme
 * la suya a mano: `nombre` ("Lote"), `mayus` ("LOTE") y `minus` ("lote").
 */
export const terminoLote = (config) => {
  const nombre = config?.termino_lote === 'lote' ? 'Lote' : 'Corral';
  return { nombre, mayus: nombre.toUpperCase(), minus: nombre.toLowerCase() };
};

/**
 * Los NEUTROS de las pantallas del catálogo: radios, bordes, sombras y grises.
 *
 * Son FORMA, no marca, y por eso van fijos. El color de acento NO sale de acá:
 * sale de `config.color_primary`, porque cada cabaña pinta su vidriera con lo
 * suyo. Lo mismo vale para la escala de los DEPs, que es un instrumento de
 * medición y vive en `utils/depsEscalones.js`.
 *
 * ESTABA COPIADO EN TRES ARCHIVOS —`CatalogoPage`, `AnimalCatalogoPage` y
 * `BloqueToro`, cada uno con su `const T = {...}`— y ya había divergido, que es
 * la forma en que este tipo de duplicación avisa: `BloqueToro` tenía las dos
 * sombras, `CatalogoPage` tenía `surface` y ninguna sombra, y
 * `AnimalCatalogoPage` había perdido `surface` y hardcodeaba la sombra dentro
 * de su bloque de estilos. Tres copias, tres formas, la misma intención.
 *
 * Las tres pantallas lo importan como `T` para no renombrar 36 usos, y porque
 * ahí adentro `T.border` ya se lee bien.
 */
export const NEUTROS = {
  surface: '#ffffff',
  border: '#e4e1db',
  pearl: '#f3f3f3',
  textDim: '#6b6b6b',
  radius: 12,
  shadowSm: '0 1px 3px rgba(35,35,35,.05)',
  shadow: '0 4px 12px rgba(35,35,35,.08)',
  // El ÚNICO que no es propio del micrositio: es el verde de estado del
  // producto, el mismo que usa el panel. Sale de `pujaPalette` en vez de
  // repetir el hex, que era una cuarta copia.
  success: pujaPalette.success,
};

// Los spans que antd sabe dibujar: tienen que DIVIDIR a 24.
//
// `columnas_catalogo` sale de la config del productor. Con 5 columnas,
// `24 / 5` da 4.8 y antd emite una clase `ant-col-lg-4.8` que no existe —la
// grilla se desarma—; con `'0'` da Infinity y con un valor no numerico, NaN.
const COLUMNAS_VALIDAS = [2, 3, 4, 6, 8, 12, 24];

/**
 * Cuantas columnas de grilla usar, ya validadas.
 *
 * Vive acá porque la usan LAS DOS grillas de animales —la del catálogo
 * (`CatalogoPage`) y la del remate (`RematePage`)— sobre la MISMA clave de
 * config. Se habia endurecido en una sola, que es blindar la puerta y dejar la
 * ventana abierta.
 *
 * @param {object} config
 * @param {number} porDefecto
 * @returns {number}
 */
export const columnasValidas = (config = {}, porDefecto = 4) => {
  const pedidas = parseInt(config.columnas_catalogo || String(porDefecto), 10);
  return COLUMNAS_VALIDAS.includes(pedidas) ? pedidas : porDefecto;
};
